/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Pressable, Icon } from '@components/atoms'
import { motion } from 'framer-motion'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

interface PropsType {
	show: boolean
	isFullModal?: boolean
	width?: number

	// header
	title: string
	renderTitle?: JSX.Element

	// event
	onCloseModal?: () => void

	children?: JSX.Element
}

/** 우측 패널 — surface + 좌측 헤어라인, 그림자 없음. 열고 닫는 폭 애니메이션은 그대로 */
export const SideModal = (props: PropsType) => {
	// state
	const [showModal, setShowModal] = React.useState<boolean>(props.show)

	// variables
	const isFullModal: boolean = props.isFullModal ?? false
	const width: number = props.width ? props.width : 20

	React.useEffect(() => {
		setShowModal(props.show)
	}, [props.show])

	const onCloseModal = () => {
		setShowModal(false)
		if (props.onCloseModal !== undefined) {
			props.onCloseModal()
		}
	}

	return (
		<motion.div
			css={style}
			className={isFullModal ? 'fullModal' : ''}
			initial={{ opacity: 0, width: 0 }}
			animate={showModal ? { opacity: 1, width: `${width}rem` } : { width: 0 }}
			exit={{ opacity: 0, width: 0 }}
			transition={{ duration: 0.2 }}
		>
			<View className="sideModalWrapper">
				<View className="headerView">
					{props.renderTitle ? (
						props.renderTitle
					) : (
						<Text as="span" className="sideModalTitle">
							{props.title}
						</Text>
					)}
					<Pressable className="closeButton" onClick={onCloseModal} aria-label="닫기">
						<Icon name="close" className="closeIcon" />
					</Pressable>
				</View>
				<View className="sideContentView">{props.children}</View>
			</View>
		</motion.div>
	)
}

const style = css({
	position: 'absolute',
	zIndex: 9,
	width: '20rem',
	top: 0,
	right: 0,
	height: '100%',
	background: mono.surface,
	borderLeft: `1px solid ${mono.line}`,
	boxShadow: 'none',
	overflow: 'hidden',
	fontFamily: boFont.ui,

	'.sideModalWrapper': {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
	},

	'.headerView': {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 8,
		width: '100%',
		height: 48,
		padding: '0 12px 0 20px',
		borderBottom: `1px solid ${mono.line}`,

		'.sideModalTitle': {
			fontFamily: boFont.ui,
			fontSize: 14,
			fontWeight: 500,
			color: mono.ink,
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
		},

		'.closeButton': {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			width: 28,
			height: 28,
			padding: 0,
			borderRadius: 2,
			'.closeIcon': { fontSize: 18, color: mono.dim },
			'&:hover .closeIcon': { color: mono.ink },
		},
	},

	'.sideContentView': {
		position: 'relative',
		flex: 1,
		minHeight: 0,
		overflow: 'auto',
	},

	'@media (max-width:900px)': {
		width: '20rem',
		height: '100%',
	},
})
